// Adjustment / Quotations / Transfer / Damages — Inventory Operations
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Search, FileEdit, Truck, AlertTriangle, Quote, ArrowLeft, Save } from "lucide-react";
import { api } from "../../../lib/auth";
import { Card, PageHeader, PrimaryButton, Field, TextInput, Select, SectionHeader } from "../products/_shared";
import { fmtMoney as fmt } from "../../../lib/currency";

// ────────────────────────── Shared: Product picker ──────────────────────────
function ProductPicker({ onPick, testid = "ip-search" }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  useEffect(() => {
    if (!q) return setRes([]);
    const t = setTimeout(() => api.get("/t/products", { params: { q, limit: 8 } }).then((r) => setRes(r.data)), 200);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative">
      <div className="flex items-center gap-2 bg-[#FAFBFB] rounded-lg px-3 py-2 border border-black/10">
        <Search size={14} className="text-black/40" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid={testid}
          placeholder="Search product by name/SKU…" className="flex-1 bg-transparent focus:outline-none text-sm" />
      </div>
      {res.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 bg-white rounded-xl border border-black/10 shadow-lg max-h-72 overflow-y-auto">
          {res.map((p) => (
            <button key={p.id} onClick={() => { onPick(p); setQ(""); setRes([]); }}
              className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-[#F4F5FA] text-left text-sm">
              <span className="font-semibold">{p.name}</span>
              <span className="text-xs text-black/55">Stock {p.stock ?? 0} · {fmt(p.unit_price)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ────────────────────────── ADJUSTMENT ──────────────────────────
export function AdjustmentsList() {
  const [rows, setRows] = useState([]);
  const { subdomain } = useParams();
  useEffect(() => { api.get("/t/adjustments").then((r) => setRows(r.data)); }, []);
  const del = async (r) => {
    if (!window.confirm("Delete adjustment?")) return;
    await api.delete(`/t/adjustments/${r.id}`);
    toast.success("Deleted");
    api.get("/t/adjustments").then((r) => setRows(r.data));
  };
  return (
    <div data-testid="adjustment-page" className="p-6 lg:p-8">
      <PageHeader title="Stock Adjustments" breadcrumb="Inventory / Adjustment" actions={
        <Link to={`/t/${subdomain}/adjustment/new`}><PrimaryButton icon={Plus} testid="adjustment-new">Create Adjustment</PrimaryButton></Link>
      } />
      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase">
            <tr>
              <th className="text-left p-3.5">Reference</th>
              <th className="text-left p-3.5">Date</th>
              <th className="text-left p-3.5">Reason</th>
              <th className="text-right p-3.5">Items</th>
              <th className="text-right p-3.5">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-[#FAFBFB]">
                <td className="p-3.5 font-semibold">{r.reference}</td>
                <td className="p-3.5">{r.date || r.created_at?.slice(0, 10)}</td>
                <td className="p-3.5">{r.reason || "—"}</td>
                <td className="p-3.5 text-right">{(r.items || []).length}</td>
                <td className="p-3.5 text-right">
                  <button onClick={() => del(r)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={5} className="p-10 text-center text-black/45">No adjustments yet</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

export function AdjustmentForm() {
  const { subdomain } = useParams();
  const nav = useNavigate();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState("");
  useEffect(() => { api.get("/t/warehouses").then((r) => { setWarehouses(r.data); setWarehouseId(r.data?.[0]?.id || ""); }); }, []);

  const add = (p) => setItems((x) => x.some((it) => it.product_id === p.id) ? x : [...x, { product_id: p.id, name: p.name, qty: 1, action: "addition" }]);
  const upd = (i, k, v) => setItems((x) => x.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const rm = (i) => setItems((x) => x.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!items.length) return toast.error("Add at least one product");
    try {
      await api.post("/t/adjustments", { date, warehouse_id: warehouseId, reason, note, items });
      toast.success("Adjustment saved");
      nav(`/t/${subdomain}/adjustment`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="adjustment-form-page" className="p-6 lg:p-8">
      <PageHeader title="Create Adjustment" breadcrumb="Inventory / Adjustment / Create" actions={
        <Link to={`/t/${subdomain}/adjustment`}><button className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl bg-white border border-black/10 text-sm font-semibold"><ArrowLeft size={13} /> Back</button></Link>
      } />
      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="p-5 lg:col-span-1">
          <SectionHeader icon={FileEdit} title="Details" hint="Reason & note" />
          <div className="space-y-3">
            <Field lbl="Date"><TextInput type="date" value={date} onChange={setDate} testid="adj-date" /></Field>
            <Field lbl="Branch">
              <Select value={warehouseId} onChange={setWarehouseId} options={warehouses.map((w) => ({ value: w.id, label: w.name }))} testid="adj-wh" />
            </Field>
            <Field lbl="Reason">
              <TextInput value={reason} onChange={setReason} placeholder="e.g. Cycle Count, Breakage" testid="adj-reason" />
            </Field>
            <Field lbl="Note">
              <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="adj-note"
                className="w-full px-3 py-2.5 rounded-lg border border-black/15 text-sm min-h-[90px]" />
            </Field>
          </div>
        </Card>
        <Card className="p-5 lg:col-span-2">
          <SectionHeader icon={Search} title="Items" hint="Search & add products" />
          <ProductPicker onPick={add} testid="adj-search" />
          <table className="w-full text-sm mt-4">
            <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase">
              <tr>
                <th className="text-left p-2">Product</th>
                <th className="text-left p-2 w-32">Action</th>
                <th className="text-right p-2 w-28">Qty</th>
                <th className="p-2 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-black/5">
              {items.map((it, i) => (
                <tr key={i}>
                  <td className="p-2 font-semibold">{it.name}</td>
                  <td className="p-2">
                    <select value={it.action} onChange={(e) => upd(i, "action", e.target.value)}
                      className="px-2 py-1.5 rounded border border-black/15 text-xs">
                      <option value="addition">Addition (+)</option>
                      <option value="subtraction">Subtraction (−)</option>
                    </select>
                  </td>
                  <td className="p-2 text-right"><input type="number" min="0" value={it.qty} onChange={(e) => upd(i, "qty", Number(e.target.value))} className="w-24 text-right px-2 py-1.5 rounded border border-black/15" /></td>
                  <td className="p-2 text-right"><button onClick={() => rm(i)} className="text-rose-600"><Trash2 size={13} /></button></td>
                </tr>
              ))}
              {!items.length && <tr><td colSpan={4} className="p-6 text-center text-black/45">No items added</td></tr>}
            </tbody>
          </table>
          <div className="mt-4 flex justify-end">
            <PrimaryButton icon={Save} onClick={submit} testid="adj-submit">Save Adjustment</PrimaryButton>
          </div>
        </Card>
      </div>
    </div>
  );
}

// ────────────────────────── QUOTATIONS ──────────────────────────
export function QuotationsList() {
  const [rows, setRows] = useState([]);
  const { subdomain } = useParams();
  useEffect(() => { api.get("/t/quotations").then((r) => setRows(r.data)); }, []);
  const del = async (r) => {
    if (!window.confirm("Delete quotation?")) return;
    await api.delete(`/t/quotations/${r.id}`);
    toast.success("Deleted");
    api.get("/t/quotations").then((r) => setRows(r.data));
  };
  const colorFor = (s) => ({
    draft: "bg-black/5 text-black/60",
    sent: "bg-sky-100 text-sky-700",
    accepted: "bg-emerald-100 text-emerald-700",
    rejected: "bg-rose-100 text-rose-700",
    expired: "bg-amber-100 text-amber-700",
  })[s] || "bg-black/5 text-black/60";
  return (
    <div data-testid="quotations-page" className="p-6 lg:p-8">
      <PageHeader title="Quotations" breadcrumb="Sales / Quotations" actions={
        <Link to={`/t/${subdomain}/quotations/new`}><PrimaryButton icon={Plus} testid="quotations-new">Create Quotation</PrimaryButton></Link>
      } />
      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase">
            <tr>
              <th className="text-left p-3.5">Reference</th>
              <th className="text-left p-3.5">Customer</th>
              <th className="text-left p-3.5">Date</th>
              <th className="text-left p-3.5">Status</th>
              <th className="text-right p-3.5">Total</th>
              <th className="text-right p-3.5">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-[#FAFBFB]">
                <td className="p-3.5 font-semibold">{r.reference}</td>
                <td className="p-3.5">{r.customer_name}</td>
                <td className="p-3.5">{r.date || r.created_at?.slice(0, 10)}</td>
                <td className="p-3.5"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${colorFor(r.status)}`}>{r.status}</span></td>
                <td className="p-3.5 text-right font-semibold">{fmt(r.total)}</td>
                <td className="p-3.5 text-right"><button onClick={() => del(r)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600"><Trash2 size={14} /></button></td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={6} className="p-10 text-center text-black/45">No quotations yet</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

export function QuotationForm() {
  const { subdomain } = useParams();
  const nav = useNavigate();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [validUntil, setValidUntil] = useState(new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10));
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [status, setStatus] = useState("draft");
  const [note, setNote] = useState("");
  const [items, setItems] = useState([]);
  const [orderTax, setOrderTax] = useState(0);
  const [orderDiscount, setOrderDiscount] = useState(0);
  const [shipping, setShipping] = useState(0);

  useEffect(() => { api.get("/t/customers").then((r) => setCustomers(r.data || [])); }, []);

  const add = (p) => setItems((x) => x.some((it) => it.product_id === p.id) ? x : [...x, { product_id: p.id, name: p.name, qty: 1, unit_price: p.unit_price || 0, discount: 0, tax: 0 }]);
  const upd = (i, k, v) => setItems((x) => x.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const rm = (i) => setItems((x) => x.filter((_, idx) => idx !== i));
  const subtotal = items.reduce((s, it) => s + (it.qty * it.unit_price - (it.discount || 0) + (it.tax || 0)), 0);
  const total = subtotal + (orderTax || 0) - (orderDiscount || 0) + (shipping || 0);

  const submit = async () => {
    if (!items.length) return toast.error("Add at least one product");
    const cust = customers.find((c) => c.id === customerId);
    try {
      await api.post("/t/quotations", {
        date, valid_until: validUntil, status, note,
        customer_id: customerId || null, customer_name: cust?.name || "Walk-in Customer",
        items, order_tax: orderTax, order_discount: orderDiscount, shipping,
      });
      toast.success("Quotation saved");
      nav(`/t/${subdomain}/quotations`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="quotation-form-page" className="p-6 lg:p-8">
      <PageHeader title="Create Quotation" breadcrumb="Sales / Quotations / Create" actions={
        <Link to={`/t/${subdomain}/quotations`}><button className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl bg-white border border-black/10 text-sm font-semibold"><ArrowLeft size={13} /> Back</button></Link>
      } />
      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="p-5 lg:col-span-1">
          <SectionHeader icon={Quote} title="Details" />
          <div className="space-y-3">
            <Field lbl="Date"><TextInput type="date" value={date} onChange={setDate} testid="q-date" /></Field>
            <Field lbl="Valid Until"><TextInput type="date" value={validUntil} onChange={setValidUntil} testid="q-valid" /></Field>
            <Field lbl="Customer">
              <Select value={customerId} onChange={setCustomerId} options={[{ value: "", label: "Walk-in Customer" }, ...customers.map((c) => ({ value: c.id, label: c.name }))]} testid="q-customer" />
            </Field>
            <Field lbl="Status">
              <Select value={status} onChange={setStatus} options={["draft", "sent", "accepted", "rejected", "expired"].map((s) => ({ value: s, label: s }))} testid="q-status" />
            </Field>
            <Field lbl="Note">
              <textarea value={note} onChange={(e) => setNote(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-black/15 text-sm min-h-[70px]" />
            </Field>
          </div>
        </Card>
        <Card className="p-5 lg:col-span-2">
          <SectionHeader icon={Search} title="Items" />
          <ProductPicker onPick={add} testid="q-search" />
          <table className="w-full text-sm mt-4">
            <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase">
              <tr>
                <th className="text-left p-2">Product</th>
                <th className="text-right p-2 w-20">Qty</th>
                <th className="text-right p-2 w-24">Price</th>
                <th className="text-right p-2 w-24">Disc</th>
                <th className="text-right p-2 w-24">Tax</th>
                <th className="text-right p-2 w-28">Total</th>
                <th className="p-2 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-black/5">
              {items.map((it, i) => (
                <tr key={i}>
                  <td className="p-2 font-semibold">{it.name}</td>
                  <td className="p-2 text-right"><input type="number" min="0" value={it.qty} onChange={(e) => upd(i, "qty", Number(e.target.value))} className="w-16 text-right px-2 py-1 rounded border border-black/15" /></td>
                  <td className="p-2 text-right"><input type="number" value={it.unit_price} onChange={(e) => upd(i, "unit_price", Number(e.target.value))} className="w-20 text-right px-2 py-1 rounded border border-black/15" /></td>
                  <td className="p-2 text-right"><input type="number" value={it.discount} onChange={(e) => upd(i, "discount", Number(e.target.value))} className="w-20 text-right px-2 py-1 rounded border border-black/15" /></td>
                  <td className="p-2 text-right"><input type="number" value={it.tax} onChange={(e) => upd(i, "tax", Number(e.target.value))} className="w-20 text-right px-2 py-1 rounded border border-black/15" /></td>
                  <td className="p-2 text-right font-semibold">{fmt(it.qty * it.unit_price - (it.discount || 0) + (it.tax || 0))}</td>
                  <td className="p-2 text-right"><button onClick={() => rm(i)} className="text-rose-600"><Trash2 size={13} /></button></td>
                </tr>
              ))}
              {!items.length && <tr><td colSpan={7} className="p-6 text-center text-black/45">No items</td></tr>}
            </tbody>
          </table>
          <div className="mt-4 grid grid-cols-3 gap-3">
            <Field lbl="Order Tax"><TextInput type="number" value={orderTax} onChange={setOrderTax} /></Field>
            <Field lbl="Order Discount"><TextInput type="number" value={orderDiscount} onChange={setOrderDiscount} /></Field>
            <Field lbl="Shipping"><TextInput type="number" value={shipping} onChange={setShipping} /></Field>
          </div>
          <div className="mt-4 flex items-center justify-between bg-[#E8F5E9] rounded-xl p-4">
            <div className="text-sm">
              <div className="text-black/55">Subtotal: <span className="font-bold text-black">{fmt(subtotal)}</span></div>
              <div className="font-extrabold text-xl text-[#1FA868]">Total: {fmt(total)}</div>
            </div>
            <PrimaryButton icon={Save} onClick={submit} testid="q-submit">Save Quotation</PrimaryButton>
          </div>
        </Card>
      </div>
    </div>
  );
}

// ────────────────────────── TRANSFER ──────────────────────────
export function TransfersList() {
  const [rows, setRows] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const { subdomain } = useParams();
  useEffect(() => {
    api.get("/t/transfers").then((r) => setRows(r.data));
    api.get("/t/warehouses").then((r) => setWarehouses(r.data));
  }, []);
  const nameOf = (id) => warehouses.find((w) => w.id === id)?.name || "—";
  const del = async (r) => {
    if (!window.confirm("Delete transfer?")) return;
    await api.delete(`/t/transfers/${r.id}`);
    toast.success("Deleted");
    api.get("/t/transfers").then((r) => setRows(r.data));
  };
  return (
    <div data-testid="transfer-page" className="p-6 lg:p-8">
      <PageHeader title="Branch Transfers" breadcrumb="Inventory / Transfer" actions={
        <Link to={`/t/${subdomain}/transfer/new`}><PrimaryButton icon={Plus} testid="transfer-new">Create Transfer</PrimaryButton></Link>
      } />
      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase">
            <tr>
              <th className="text-left p-3.5">Reference</th>
              <th className="text-left p-3.5">Date</th>
              <th className="text-left p-3.5">From</th>
              <th className="text-left p-3.5">To</th>
              <th className="text-right p-3.5">Items</th>
              <th className="text-right p-3.5">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-[#FAFBFB]">
                <td className="p-3.5 font-semibold">{r.reference}</td>
                <td className="p-3.5">{r.date || r.created_at?.slice(0, 10)}</td>
                <td className="p-3.5">{nameOf(r.from_warehouse_id)}</td>
                <td className="p-3.5">{nameOf(r.to_warehouse_id)}</td>
                <td className="p-3.5 text-right">{(r.items || []).length}</td>
                <td className="p-3.5 text-right"><button onClick={() => del(r)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600"><Trash2 size={14} /></button></td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={6} className="p-10 text-center text-black/45">No transfers</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

export function TransferForm() {
  const { subdomain } = useParams();
  const nav = useNavigate();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [warehouses, setWarehouses] = useState([]);
  const [fromW, setFromW] = useState("");
  const [toW, setToW] = useState("");
  const [note, setNote] = useState("");
  const [items, setItems] = useState([]);

  useEffect(() => { api.get("/t/warehouses").then((r) => { setWarehouses(r.data); if (r.data?.[0]) setFromW(r.data[0].id); if (r.data?.[1]) setToW(r.data[1].id); }); }, []);

  const add = (p) => setItems((x) => x.some((it) => it.product_id === p.id) ? x : [...x, { product_id: p.id, name: p.name, qty: 1 }]);
  const upd = (i, k, v) => setItems((x) => x.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const rm = (i) => setItems((x) => x.filter((_, idx) => idx !== i));
  const submit = async () => {
    if (!fromW || !toW) return toast.error("Choose branches");
    if (fromW === toW) return toast.error("From / To must differ");
    if (!items.length) return toast.error("Add at least one product");
    try {
      await api.post("/t/transfers", { date, from_warehouse_id: fromW, to_warehouse_id: toW, items, note, status: "completed" });
      toast.success("Transfer saved");
      nav(`/t/${subdomain}/transfer`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div data-testid="transfer-form-page" className="p-6 lg:p-8">
      <PageHeader title="Create Transfer" breadcrumb="Inventory / Transfer / Create" actions={
        <Link to={`/t/${subdomain}/transfer`}><button className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl bg-white border border-black/10 text-sm font-semibold"><ArrowLeft size={13} /> Back</button></Link>
      } />
      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="p-5 lg:col-span-1">
          <SectionHeader icon={Truck} title="From / To" />
          <div className="space-y-3">
            <Field lbl="Date"><TextInput type="date" value={date} onChange={setDate} testid="tr-date" /></Field>
            <Field lbl="From Branch">
              <Select value={fromW} onChange={setFromW} options={warehouses.map((w) => ({ value: w.id, label: w.name }))} testid="tr-from" />
            </Field>
            <Field lbl="To Branch">
              <Select value={toW} onChange={setToW} options={warehouses.map((w) => ({ value: w.id, label: w.name }))} testid="tr-to" />
            </Field>
            <Field lbl="Note">
              <textarea value={note} onChange={(e) => setNote(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-black/15 text-sm min-h-[70px]" />
            </Field>
          </div>
        </Card>
        <Card className="p-5 lg:col-span-2">
          <SectionHeader icon={Search} title="Items to Transfer" />
          <ProductPicker onPick={add} testid="tr-search" />
          <table className="w-full text-sm mt-4">
            <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase">
              <tr>
                <th className="text-left p-2">Product</th>
                <th className="text-right p-2 w-28">Qty</th>
                <th className="p-2 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-black/5">
              {items.map((it, i) => (
                <tr key={i}>
                  <td className="p-2 font-semibold">{it.name}</td>
                  <td className="p-2 text-right"><input type="number" min="0" value={it.qty} onChange={(e) => upd(i, "qty", Number(e.target.value))} className="w-24 text-right px-2 py-1.5 rounded border border-black/15" /></td>
                  <td className="p-2 text-right"><button onClick={() => rm(i)} className="text-rose-600"><Trash2 size={13} /></button></td>
                </tr>
              ))}
              {!items.length && <tr><td colSpan={3} className="p-6 text-center text-black/45">No items</td></tr>}
            </tbody>
          </table>
          <div className="mt-4 flex justify-end">
            <PrimaryButton icon={Save} onClick={submit} testid="tr-submit">Save Transfer</PrimaryButton>
          </div>
        </Card>
      </div>
    </div>
  );
}

// ────────────────────────── DAMAGES ──────────────────────────
export function DamagesList() {
  const [rows, setRows] = useState([]);
  const { subdomain } = useParams();
  useEffect(() => { api.get("/t/damages").then((r) => setRows(r.data)); }, []);
  const del = async (r) => {
    if (!window.confirm("Delete damage entry?")) return;
    await api.delete(`/t/damages/${r.id}`);
    toast.success("Deleted");
    api.get("/t/damages").then((r) => setRows(r.data));
  };
  return (
    <div data-testid="damages-page" className="p-6 lg:p-8">
      <PageHeader title="Damaged Stock" breadcrumb="Inventory / Damages" actions={
        <Link to={`/t/${subdomain}/damages/new`}><PrimaryButton icon={Plus} testid="damages-new">Log Damage</PrimaryButton></Link>
      } />
      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase">
            <tr>
              <th className="text-left p-3.5">Reference</th>
              <th className="text-left p-3.5">Date</th>
              <th className="text-left p-3.5">Note</th>
              <th className="text-right p-3.5">Items</th>
              <th className="text-right p-3.5">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-[#FAFBFB]">
                <td className="p-3.5 font-semibold">{r.reference}</td>
                <td className="p-3.5">{r.date || r.created_at?.slice(0, 10)}</td>
                <td className="p-3.5">{r.note || "—"}</td>
                <td className="p-3.5 text-right">{(r.items || []).length}</td>
                <td className="p-3.5 text-right"><button onClick={() => del(r)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600"><Trash2 size={14} /></button></td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={5} className="p-10 text-center text-black/45">No damage records</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

export function DamageForm() {
  const { subdomain } = useParams();
  const nav = useNavigate();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [wh, setWh] = useState("");
  useEffect(() => { api.get("/t/warehouses").then((r) => { setWarehouses(r.data); setWh(r.data?.[0]?.id || ""); }); }, []);
  const add = (p) => setItems((x) => x.some((it) => it.product_id === p.id) ? x : [...x, { product_id: p.id, name: p.name, qty: 1, reason: "" }]);
  const upd = (i, k, v) => setItems((x) => x.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const rm = (i) => setItems((x) => x.filter((_, idx) => idx !== i));
  const submit = async () => {
    if (!items.length) return toast.error("Add at least one product");
    try {
      await api.post("/t/damages", { date, warehouse_id: wh, note, items });
      toast.success("Damage logged — stock deducted");
      nav(`/t/${subdomain}/damages`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div data-testid="damages-form-page" className="p-6 lg:p-8">
      <PageHeader title="Log Damage" breadcrumb="Inventory / Damages / Create" actions={
        <Link to={`/t/${subdomain}/damages`}><button className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl bg-white border border-black/10 text-sm font-semibold"><ArrowLeft size={13} /> Back</button></Link>
      } />
      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="p-5 lg:col-span-1">
          <SectionHeader icon={AlertTriangle} title="Damage Details" />
          <div className="space-y-3">
            <Field lbl="Date"><TextInput type="date" value={date} onChange={setDate} testid="dmg-date" /></Field>
            <Field lbl="Branch">
              <Select value={wh} onChange={setWh} options={warehouses.map((w) => ({ value: w.id, label: w.name }))} testid="dmg-wh" />
            </Field>
            <Field lbl="Note">
              <textarea value={note} onChange={(e) => setNote(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-black/15 text-sm min-h-[90px]" />
            </Field>
          </div>
        </Card>
        <Card className="p-5 lg:col-span-2">
          <SectionHeader icon={Search} title="Damaged Items" hint="Stock auto-deducts on save" />
          <ProductPicker onPick={add} testid="dmg-search" />
          <table className="w-full text-sm mt-4">
            <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase">
              <tr>
                <th className="text-left p-2">Product</th>
                <th className="text-right p-2 w-24">Qty</th>
                <th className="text-left p-2 w-56">Reason</th>
                <th className="p-2 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-black/5">
              {items.map((it, i) => (
                <tr key={i}>
                  <td className="p-2 font-semibold">{it.name}</td>
                  <td className="p-2 text-right"><input type="number" min="0" value={it.qty} onChange={(e) => upd(i, "qty", Number(e.target.value))} className="w-20 text-right px-2 py-1.5 rounded border border-black/15" /></td>
                  <td className="p-2"><input value={it.reason} onChange={(e) => upd(i, "reason", e.target.value)} placeholder="e.g. broken, expired" className="w-full px-2 py-1.5 rounded border border-black/15" /></td>
                  <td className="p-2 text-right"><button onClick={() => rm(i)} className="text-rose-600"><Trash2 size={13} /></button></td>
                </tr>
              ))}
              {!items.length && <tr><td colSpan={4} className="p-6 text-center text-black/45">No items</td></tr>}
            </tbody>
          </table>
          <div className="mt-4 flex justify-end">
            <PrimaryButton icon={Save} onClick={submit} testid="dmg-submit">Save</PrimaryButton>
          </div>
        </Card>
      </div>
    </div>
  );
}
